/**
 * RefundModal.tsx - DonationModel Refund Processing Modal
 *
 * This component provides an interface for processing DonationModel refunds
 * with donor information display, refund reason selection, and confirmation.
 *
 * Key Features:
 * - DonationModel details display with donor information
 * - Refund reason selection and notes
 * - Partial and full refund options
 * - Refund confirmation and processing
 * - Payment method display and refund routing
 * - Audit trail and documentation
 *
 * Refund Process:
 * - Display original DonationModel details
 * - Select refund reason from predefined options
 * - Enter refund amount (partial or full)
 * - Add optional refund notes
 * - Confirm and process refund
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - DonationModel: DonationModel | null - DonationModel to refund
 * - onRefund: (refundData: RefundData) => void - Refund processing handler
 *
 * Dependencies: DonationModel types, date utilities, portal rendering
 */

import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  User,
  CreditCard,
  CaseSensitive as University,
  RotateCcw,
} from "lucide-react";
import { formatDateForDisplay } from "../utils/dateUtils";
import { Toast } from "./Toast";
import {
  ModalOverlay,
  WideModalContent,
  FormGroup,
  FormLabel,
  FormSection,
  PrimaryButton,
  SecondaryButton,
} from "./shared/StyledComponents";
import styled from "styled-components";
import { DonationModel, FundraisersDonationsBoardResponse } from "../models";
import { AuthService } from "@mindmemobile/mm-shared-global";
import { FundraiserService } from "../services";
import { firstValueFrom } from "rxjs";
import achCard from "../../../../images/payment/ach_card_logo.svg";
import amexCard from "../../../../images/payment/amex_card_logo.svg";
import masterCard from "../../../../images/payment/master_card_logo.svg";
import visaCard from "../../../../images/payment/visa-card_logo.svg";
import discoverIcon from "../../../../images/payment/discover.svg";
import ToastService from "./ToastService";
import { canManageFundraiser } from "../utils/permissions";

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalHeader = styled.div`
  padding: 20px 40px 14px;
  margin-bottom: 24px;
`;

const ModalTitleBar = styled.div`
  background: #fef2f2;
  padding: 32px 32px 16px;
  height: 100px;
  text-align: center;
  border-radius: 24px 24px 0 0;
  border-bottom: 1px solid #f1f5f9;
  position: relative;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #dc2626;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const RefundAmount = styled.div`
  font-size: 36px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  margin-bottom: 4px;
`;

const PaymentDetails = styled.div`
  font-size: 16px;
  color: #1e293b;
  text-align: center;
  margin: -4px 0 0 0;
`;

const ContentContainer = styled.div<{ hasCheckbox: boolean }>`
  padding: 35px 40px ${(props) => (props.hasCheckbox ? "32px" : "16px")};
`;

const PreviousRefundDisplay = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #1e293b;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const CurrencyInputContainer = styled.div`
  position: relative;
`;

const CurrencySymbol = styled.span<{ hasError?: boolean }>`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.hasError ? "#dc2626" : "#64748b")};
`;

const CurrencyInput = styled.input`
  width: 100%;
  padding: 16px 16px 16px 40px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CurrencyInputWithError = styled.input<{ hasError: boolean }>`
  width: 100%;
  padding: 16px 60px 16px 40px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 12px;
  font-size: 16px;
  color: ${(props) => (props.hasError ? "#dc2626" : "#1e293b")};
  transition: all 0.2s ease;
  background: ${(props) => (props.hasError ? "#fef2f2" : "#ffffff")};

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#3b82f6")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(59, 130, 246, 0.1)"};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ErrorText = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 16px;
  font-size: 12px;
  font-weight: 600;
  color: #dc2626;
  pointer-events: none;
  white-space: nowrap;
`;

const PaymentMethodDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  height: 56px;
`;

const PaymentMethodIcon = styled.div`
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;
  padding: 4px;

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
  }
`;

const PaymentMethodAccount = styled.div`
  font-size: 18px;
  color: #1e293b;
  font-weight: 600;
`;
const TextArea = styled.textarea`
  width: 100%;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CheckboxSection = styled.div<{ hasOtherReason?: boolean }>`
  margin-bottom: 16px;
  margin-top: ${(props) => (props.hasOtherReason ? "15px" : "30px")};
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 16px;
  color: #1e293b;
  font-weight: 400;
  line-height: 1.4;
`;

const BoldAmount = styled.span`
  font-weight: 700;
  color: #dc2626;
`;

const Checkbox = styled.input`
  font-size: 36px;
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #dc2626;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 10px 40px 44px;
`;

const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 275px 1fr;
  gap: 50px;
  align-items: start;
`;

const ThreeColumnRow = styled.div`
  display: grid;
  grid-template-columns: 275px 275px 1fr;
  gap: 50px;
  align-items: start;
`;

const RefundReasonRow = styled.div`
  display: grid;
  grid-template-columns: 275px 1fr;
  gap: 70px;
  align-items: start;
`;

const Select = styled.select`
  width: 100%;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CompactTextArea = styled.textarea`
  width: 100%;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const FixedWidthInput = styled.div`
  width: 275px;
`;

const DangerButton = styled(PrimaryButton)`
  background: #dc2626;

  &:hover:not(:disabled) {
    background: #b91c1c;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

interface RefundModalProps {
  isVisible: boolean;
  donation: FundraisersDonationsBoardResponse | null;
  onClose: (refresh?: boolean) => void;
}

// Sort reasons alphabetically, but keep "Other" at the end
const REFUND_REASONS = [
  "Chargeback received",
  "Donor requested refund",
  "Duplicate payment",
  "Event cancelled",
  "Fraudulent transaction",
  "Processing error",
  "Other",
];

export const RefundModal: React.FC<RefundModalProps> = ({
  isVisible,
  donation,
  onClose,
}) => {
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReasonSelect, setRefundReasonSelect] = useState("");
  const [refundReasonText, setRefundReasonText] = useState("");
  const [confirmRefund, setConfirmRefund] = useState(false);
  // In-flight lock (F06): a ref changes synchronously, so a fast double-click cannot send two refunds
  // before the disabled state re-renders.
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  // Reset form when modal opens
  React.useEffect(() => {
    if (isVisible) {
      setRefundAmount("");
      setRefundReasonSelect("");
      setRefundReasonText("");
      setConfirmRefund(false);
    }
  }, [isVisible, DonationModel]);

  if (!DonationModel) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getInitials = (name: string) => {
    if (!name || name === "Anonymous Donor") {
      return "AD";
    }
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const formatCurrencyInput = (value: string) => {
    const numericValue = value.replace(/[^0-9.]/g, "");
    if (numericValue === "") return "";

    // Return the numeric value as-is to preserve decimal places while typing
    return numericValue;
  };

  const handleRefundAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    setRefundAmount(value);
  };

  const handleRefundAmountBlur = () => {
    if (refundAmount && !isNaN(parseFloat(refundAmount))) {
      const formatted = parseFloat(refundAmount).toFixed(2);
      setRefundAmount(formatted);
    }
  };
  const validateRefundAmount = () => {
    const amount = parseFloat(refundAmount);
    const maxRefund = donation?.amount || 0;

    if (refundAmount.trim() === "" || isNaN(amount)) {
      return { isValid: true, errorMessage: "" };
    }

    if (amount <= 0) {
      return {
        isValid: false,
        errorMessage: `Refund $0.01 - ${formatCurrency(maxRefund).replace("$", "$")}`,
      };
    }

    if (amount > maxRefund) {
      return {
        isValid: false,
        errorMessage: `Refund $0.01 - ${formatCurrency(maxRefund).replace("$", "$")}`,
      };
    }

    return { isValid: true, errorMessage: "" };
  };

  const hasValidReason = () => {
    return (
      refundReasonSelect !== "" &&
      (refundReasonSelect !== "Other" || refundReasonText.trim() !== "")
    );
  };

  const shouldShowCheckbox = () => {
    const amount = parseFloat(refundAmount);
    const maxRefund = donation?.amount || 0;
    const validation = validateRefundAmount();

    return (
      refundAmount.trim() !== "" &&
      !isNaN(amount) &&
      amount > 0 &&
      amount <= maxRefund &&
      validation.isValid &&
      hasValidReason()
    );
  };

  const isFormValid = () => {
    return shouldShowCheckbox() && confirmRefund;
  };

  const getPaymentMethodIcon = () => {
    if (donation?.paymentMethod?.type === "Card") {
      const brand = donation?.paymentMethod.card?.brand?.toLowerCase();
      switch (brand) {
        case "visa":
          return visaCard;
        case "mastercard":
          return masterCard;
        case "amex":
          return amexCard;
        case "discover":
          return discoverIcon;
        default:
          return visaCard;
      }
    } else {
      return "/bank-icon.svg";
    }
  };

  const renderPaymentMethod = () => {
    const iconSrc = getPaymentMethodIcon();

    return (
      <PaymentMethodDisplay>
        <PaymentMethodIcon>
          <img
            src={iconSrc}
            alt={
              donation?.paymentMethod?.type === "Card"
                ? donation?.paymentMethod?.card?.brand
                : "Bank"
            }
          />
        </PaymentMethodIcon>
        <PaymentMethodAccount>
          ••••{" "}
          {donation?.paymentMethod.type === "Card"
            ? donation?.paymentMethod.card?.last4
            : donation?.paymentMethod.card?.cvvCheck}
        </PaymentMethodAccount>
      </PaymentMethodDisplay>
    );
  };

  const handleRefundSubmit = async () => {
    if (submittingRef.current) return;
    if (!canManageFundraiser("Refund")) return;
    if (!isFormValid()) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const finalReason =
        refundReasonSelect === "Other" ? refundReasonText : refundReasonSelect;
      const response = await firstValueFrom(
        FundraiserService.refundPayment({
          paymentTransactionId: donation?.paymentTransactionId || "",
          amount: parseFloat(refundAmount),
          description: finalReason,
          accountId: AuthService.authData.accountId,
        })
      );
      if (response.result?.success) {
        ToastService.showSuccess("Refund processed successfully");
        onClose(true);
      } else {
        ToastService.showError("Error processing refund");
      }
    } catch {
      // Network / server error: the outcome is not confirmed, so do not say it failed for sure.
      ToastService.showError("Refund status unknown. Check the donation before trying again.");
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // Mock previous refunds (in real app, this would come from API)
  const previousRefunds = donation?.amountRefunded || 0; // Assuming no previous refunds for now

  const validation = validateRefundAmount();
  const refundAmountValue = parseFloat(refundAmount) || 0;
  return createPortal(
    <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
      <WideModalContent>
        <CloseButton onClick={() => onClose(false)}>
          <X size={16} />
        </CloseButton>

        <ModalTitleBar>
          <ModalTitle>
            <RotateCcw size={26} color="#dc2626" />
            Refund Payment
          </ModalTitle>
        </ModalTitleBar>

        <ModalHeader>
          <RefundAmount>{formatCurrency(donation?.amount || 0)}</RefundAmount>
          <PaymentDetails>
            Paid by {donation?.getDisplayName()} on{" "}
            {formatDateForDisplay(donation?.timestamp || "")}
          </PaymentDetails>
        </ModalHeader>

        <ContentContainer
          hasCheckbox={validation.isValid && refundAmountValue > 0}
        >
          <FormSection style={{ marginTop: "-20px", marginBottom: "35px" }}>
            <TwoColumnRow>
              <div>
                <FormLabel>Previously Refunded</FormLabel>
                <FixedWidthInput>
                  <PreviousRefundDisplay>
                    {formatCurrency(previousRefunds)}
                  </PreviousRefundDisplay>
                </FixedWidthInput>
              </div>
              <div>
                <FormLabel htmlFor="refundAmount">Refund Amount</FormLabel>
                <FixedWidthInput>
                  <CurrencyInputContainer>
                    <CurrencySymbol hasError={!validation.isValid}>
                      $
                    </CurrencySymbol>
                    <CurrencyInputWithError
                      id="refundAmount"
                      type="text"
                      placeholder="0.00"
                      hasError={!validation.isValid}
                      value={
                        refundAmount ? formatCurrencyInput(refundAmount) : ""
                      }
                      onChange={handleRefundAmountChange}
                      onBlur={handleRefundAmountBlur}
                    />
                    {!validation.isValid && validation.errorMessage && (
                      <ErrorText>{validation.errorMessage}</ErrorText>
                    )}
                  </CurrencyInputContainer>
                </FixedWidthInput>
              </div>
            </TwoColumnRow>
          </FormSection>

          <FormSection>
            <ThreeColumnRow>
              <div>
                <FormLabel>Refund To</FormLabel>
                <FixedWidthInput>{renderPaymentMethod()}</FixedWidthInput>
              </div>
              <div>
                <FormLabel htmlFor="refundReasonSelect">
                  Refund Reason
                </FormLabel>
                <FixedWidthInput>
                  <Select
                    id="refundReasonSelect"
                    value={refundReasonSelect}
                    onChange={(e) => setRefundReasonSelect(e.target.value)}
                  >
                    <option value="">Select reason...</option>
                    {REFUND_REASONS.map((reason) => (
                      <option key={reason} value={reason}>
                        {reason}
                      </option>
                    ))}
                  </Select>
                </FixedWidthInput>
              </div>
              <div></div>
            </ThreeColumnRow>
          </FormSection>

          {refundReasonSelect === "Other" && (
            <FormSection>
              <FormLabel htmlFor="refundReasonText">Other Reason</FormLabel>
              <CompactTextArea
                id="refundReasonText"
                placeholder="Enter the reason for this refund..."
                value={refundReasonText}
                onChange={(e) => setRefundReasonText(e.target.value)}
              />
            </FormSection>
          )}

          {shouldShowCheckbox() && (
            <CheckboxSection hasOtherReason={refundReasonSelect === "Other"}>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={confirmRefund}
                  onChange={(e) => setConfirmRefund(e.target.checked)}
                />
                <div>
                  Process{" "}
                  <BoldAmount>{formatCurrency(refundAmountValue)}</BoldAmount>{" "}
                  refund now. This action cannot be reversed.
                </div>
              </CheckboxContainer>
            </CheckboxSection>
          )}
        </ContentContainer>

        <ModalActions>
          <SecondaryButton onClick={() => onClose(false)}>
            Do Not Refund
          </SecondaryButton>
          <DangerButton onClick={handleRefundSubmit} disabled={!isFormValid() || isSubmitting}>
            {isSubmitting ? "Processing..." : "Process Refund"}
          </DangerButton>
        </ModalActions>
      </WideModalContent>
    </ModalOverlay>,
    document.body
  );
};
